import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { sendClaimDecisionEmail } from '../lib/emails';

const ADMIN_USER_IDS = [
  'b362e9ab-12c7-4a78-af64-410288835c20', // BetaAdmin
];

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const AdminLocationClaims = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [denyingId, setDenyingId] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [adminNotes, setAdminNotes] = useState({});

  // Check admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) { navigate('/'); return; }

      const { data: profile } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (profile && ADMIN_USER_IDS.includes(profile.id)) {
        setIsAdmin(true);
      } else if (ADMIN_USER_IDS.length === 0) {
        setIsAdmin(true);
      } else {
        navigate('/');
      }
    };
    checkAdmin();
  }, [user, navigate]);

  // Fetch claims
  useEffect(() => {
    if (!isAdmin) return;

    const fetchClaims = async () => {
      setLoading(true);

      let query = supabase
        .from('location_claims')
        .select(`
          *,
          location:locations(id, store_name, address, city, state, zip_code, phone, website, verified, owner_id),
          claimant:users!location_claims_claimant_id_fkey(id, username, email, avatar_url)
        `)
        .order('submitted_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error('Error fetching claims:', error);
      } else {
        setClaims(data || []);
        // Load admin notes
        const notes = {};
        (data || []).forEach(c => { if (c.admin_notes) notes[c.id] = c.admin_notes; });
        setAdminNotes(notes);
      }

      setLoading(false);
    };

    fetchClaims();
  }, [isAdmin, filter]);

  // Get admin profile ID
  const getAdminProfileId = async () => {
    const { data } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    return data?.id;
  };

  const handleApprove = async (claim) => {
    setProcessing(claim.id);

    try {
      const adminId = await getAdminProfileId();
      const now = new Date().toISOString();

      // Save admin notes if any
      const notes = adminNotes[claim.id]?.trim() || null;

      // Update claim status
      await supabase
        .from('location_claims')
        .update({
          status: 'approved',
          reviewed_at: now,
          reviewed_by: adminId,
          admin_notes: notes,
        })
        .eq('id', claim.id);

      // Set location owner_id + verified (this triggers create_owner_staff_record)
      await supabase
        .from('locations')
        .update({
          owner_id: claim.claimant_auth_id,
          verified: true,
          application_approved: true,
          reviewed_at: now,
          reviewed_by: adminId,
        })
        .eq('id', claim.location_id);

      // Send approval email (non-blocking)
      sendClaimDecisionEmail(claim.claimant_id, claim.location_id, true).catch(err =>
        console.error('[Email] Approval email failed:', err)
      );

      // Update local state
      setClaims(prev => prev.map(c =>
        c.id === claim.id ? { ...c, status: 'approved', reviewed_at: now } : c
      ));
    } catch (err) {
      console.error('Error approving claim:', err);
      alert('Failed to approve claim');
    }

    setProcessing(null);
  };

  const handleDeny = async (claim) => {
    if (!denyReason.trim()) {
      alert('Please provide a reason for denial');
      return;
    }

    setProcessing(claim.id);

    try {
      const adminId = await getAdminProfileId();
      const now = new Date().toISOString();
      const notes = adminNotes[claim.id]?.trim() || null;

      await supabase
        .from('location_claims')
        .update({
          status: 'denied',
          denial_reason: denyReason.trim(),
          reviewed_at: now,
          reviewed_by: adminId,
          admin_notes: notes,
        })
        .eq('id', claim.id);

      // Send denial email (non-blocking)
      sendClaimDecisionEmail(claim.claimant_id, claim.location_id, false, denyReason.trim()).catch(err =>
        console.error('[Email] Denial email failed:', err)
      );

      setClaims(prev => prev.map(c =>
        c.id === claim.id ? { ...c, status: 'denied', denial_reason: denyReason.trim(), reviewed_at: now } : c
      ));
      setDenyingId(null);
      setDenyReason('');
    } catch (err) {
      console.error('Error denying claim:', err);
      alert('Failed to deny claim');
    }

    setProcessing(null);
  };

  const saveAdminNotes = async (claimId) => {
    const notes = adminNotes[claimId]?.trim() || null;
    await supabase
      .from('location_claims')
      .update({ admin_notes: notes })
      .eq('id', claimId);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const styles = {
    container: { minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      backgroundColor: theme.bgCard, padding: '12px 16px', display: 'flex',
      alignItems: 'center', gap: 12, borderBottom: `1px solid ${theme.border}`,
      position: 'sticky', top: 0, zIndex: 10,
    },
    backButton: {
      background: 'none', border: 'none', padding: 8, cursor: 'pointer',
      color: theme.accent, display: 'flex',
    },
    headerTitle: { fontSize: 18, fontWeight: 600, color: theme.text, margin: 0 },
    filterRow: {
      display: 'flex', gap: 8, padding: '12px 16px', overflowX: 'auto',
      borderBottom: `1px solid ${theme.border}`, backgroundColor: theme.bgCard,
    },
    filterBtn: {
      padding: '8px 16px', borderRadius: 20, border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard, color: theme.textSecondary, fontSize: 13,
      fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
    },
    filterBtnActive: { backgroundColor: theme.accent, borderColor: theme.accent, color: '#fff' },
    content: { padding: 16 },
    emptyState: { textAlign: 'center', padding: 40, color: theme.textMuted },
    card: {
      backgroundColor: theme.bgCard, borderRadius: 12, border: `1px solid ${theme.border}`,
      marginBottom: 12, overflow: 'hidden',
    },
    cardHeader: {
      padding: 12, borderBottom: `1px solid ${theme.border}`, display: 'flex',
      justifyContent: 'space-between', alignItems: 'center',
    },
    storeName: { fontSize: 16, fontWeight: 600, color: theme.text },
    date: { fontSize: 12, color: theme.textMuted },
    cardBody: { padding: 12 },
    infoRow: { marginBottom: 10 },
    infoLabel: { fontSize: 11, color: theme.textMuted, textTransform: 'uppercase' },
    infoValue: { fontSize: 13, color: theme.text },
    notesArea: {
      width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput, color: theme.text, fontSize: 13,
      boxSizing: 'border-box', outline: 'none', minHeight: 60, resize: 'vertical',
      fontFamily: 'inherit', marginTop: 4,
    },
    saveNotesBtn: {
      fontSize: 12, color: theme.accent, background: 'none', border: 'none',
      cursor: 'pointer', padding: '4px 0', marginTop: 4,
    },
    actions: {
      padding: 12, borderTop: `1px solid ${theme.border}`, display: 'flex', gap: 8,
    },
    approveBtn: {
      flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none',
      backgroundColor: '#22c55e', color: '#fff', fontSize: 14, fontWeight: 600,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    },
    denyBtn: {
      flex: 1, padding: '10px 16px', borderRadius: 8, border: `1px solid ${theme.error}`,
      backgroundColor: 'transparent', color: theme.error, fontSize: 14, fontWeight: 600,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    },
    denyReasonArea: {
      width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${theme.error}`,
      backgroundColor: theme.bgInput, color: theme.text, fontSize: 13,
      boxSizing: 'border-box', outline: 'none', minHeight: 60, resize: 'vertical',
      fontFamily: 'inherit', marginBottom: 8,
    },
    confirmDenyBtn: {
      width: '100%', padding: '10px 16px', borderRadius: 8, border: 'none',
      backgroundColor: '#ef4444', color: '#fff', fontSize: 14, fontWeight: 600,
      cursor: 'pointer',
    },
    cancelDenyBtn: {
      width: '100%', padding: '10px 16px', borderRadius: 8, border: `1px solid ${theme.border}`,
      backgroundColor: 'transparent', color: theme.textSecondary, fontSize: 14,
      cursor: 'pointer', marginTop: 6,
    },
    statusBadge: { padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
    statusApproved: { backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' },
    statusDenied: { backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' },
    statusPending: { backgroundColor: 'rgba(250, 204, 21, 0.15)', color: '#eab308' },
    loading: { textAlign: 'center', padding: 40, color: theme.textMuted },
    agreementBadge: {
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
      color: '#22c55e', fontWeight: 600,
    },
  };

  if (!isAdmin) {
    return <div style={styles.container}><div style={styles.loading}>Checking permissions...</div></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}><BackIcon /></button>
        <h1 style={styles.headerTitle}>Location Claims</h1>
      </div>

      <div style={styles.filterRow}>
        {['pending', 'approved', 'denied', 'all'].map((f) => (
          <button
            key={f}
            style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div style={styles.content}>
        {loading ? (
          <div style={styles.loading}>Loading claims...</div>
        ) : claims.length === 0 ? (
          <div style={styles.emptyState}>No {filter === 'all' ? '' : filter} claims found</div>
        ) : (
          claims.map((claim) => (
            <div key={claim.id} style={styles.card}>
              {/* Header */}
              <div style={styles.cardHeader}>
                <div>
                  <div style={styles.storeName}>{claim.location?.store_name || 'Unknown Location'}</div>
                  <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 2 }}>
                    {claim.location?.address}, {claim.location?.city}, {claim.location?.state}
                  </div>
                </div>
                <span style={styles.date}>{formatDate(claim.submitted_at)}</span>
              </div>

              {/* Body */}
              <div style={styles.cardBody}>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Claimant</div>
                  <div style={styles.infoValue}>
                    {claim.full_name} (@{claim.claimant?.username || 'unknown'})
                  </div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Role</div>
                  <div style={styles.infoValue}>{claim.role_at_business}</div>
                </div>
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Contact</div>
                  <div style={styles.infoValue}>
                    {claim.contact_email}
                    {claim.contact_phone && ` | ${claim.contact_phone}`}
                  </div>
                </div>
                {claim.additional_notes && (
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Notes from Claimant</div>
                    <div style={{ ...styles.infoValue, fontStyle: 'italic', color: theme.textSecondary }}>
                      "{claim.additional_notes}"
                    </div>
                  </div>
                )}
                <div style={styles.infoRow}>
                  <div style={styles.infoLabel}>Agreement</div>
                  <div style={styles.agreementBadge}>
                    <ShieldIcon /> Signed (v1.0)
                  </div>
                </div>

                {/* Status for non-pending */}
                {claim.status !== 'pending' && (
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Status</div>
                    <span style={{
                      ...styles.statusBadge,
                      ...(claim.status === 'approved' ? styles.statusApproved : styles.statusDenied),
                    }}>
                      {claim.status}
                    </span>
                  </div>
                )}

                {claim.denial_reason && (
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Denial Reason</div>
                    <div style={{ ...styles.infoValue, color: theme.error }}>{claim.denial_reason}</div>
                  </div>
                )}

                {/* Admin Notes */}
                <div style={{ marginTop: 12 }}>
                  <div style={styles.infoLabel}>Admin Notes</div>
                  <textarea
                    placeholder="Internal notes about this claim..."
                    value={adminNotes[claim.id] || ''}
                    onChange={(e) => setAdminNotes(prev => ({ ...prev, [claim.id]: e.target.value }))}
                    style={styles.notesArea}
                  />
                  <button style={styles.saveNotesBtn} onClick={() => saveAdminNotes(claim.id)}>
                    Save Notes
                  </button>
                </div>
              </div>

              {/* Actions (pending only) */}
              {claim.status === 'pending' && (
                <div style={styles.actions}>
                  {denyingId === claim.id ? (
                    <div style={{ width: '100%' }}>
                      <textarea
                        placeholder="Reason for denial (required)..."
                        value={denyReason}
                        onChange={(e) => setDenyReason(e.target.value)}
                        style={styles.denyReasonArea}
                        autoFocus
                      />
                      <button
                        style={styles.confirmDenyBtn}
                        onClick={() => handleDeny(claim)}
                        disabled={processing === claim.id}
                      >
                        {processing === claim.id ? 'Processing...' : 'Confirm Denial'}
                      </button>
                      <button
                        style={styles.cancelDenyBtn}
                        onClick={() => { setDenyingId(null); setDenyReason(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        style={styles.approveBtn}
                        onClick={() => handleApprove(claim)}
                        disabled={processing === claim.id}
                      >
                        <CheckIcon />
                        {processing === claim.id ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        style={styles.denyBtn}
                        onClick={() => setDenyingId(claim.id)}
                        disabled={processing === claim.id}
                      >
                        <XIcon />
                        Deny
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminLocationClaims;
